import { useCallback, useState } from 'react'

import { SanityFilterGroup } from '@data/sanity/queries/types/modules'
import { HasCollectionStrings } from '@data/sanity/queries/types/site'
import { UrlParameter } from '@lib/hooks'
import { Filter } from '@lib/product'

import Button from '@components/buttons/button'
import Drawer, { DrawerDirection } from '@components/drawer'
import CollectionFilterGroup from './filter-group'

interface CollectionFilterProps extends HasCollectionStrings {
  filterGroups: SanityFilterGroup[]
  activeFilters: Filter[]
  activeFilterValueCount: number
  itemTotal: number
  onChange: (newParameters: UrlParameter[]) => void
}

const CollectionFilter = ({
  collectionStrings,
  filterGroups,
  activeFilters = [],
  activeFilterValueCount,
  itemTotal,
  onChange,
}: CollectionFilterProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const openFilterDrawer = useCallback(() => setIsDrawerOpen(true), [])
  const closeFilterDrawer = useCallback(() => setIsDrawerOpen(false), [])
  const clearFilters = () =>
    onChange(activeFilters.map(({ name }) => ({ name, value: null })))

  return (
    <>
      <div className="flex items-center px-2 sm:px-4">
        <button
          onClick={openFilterDrawer}
          className="appearance-none no-underline cursor-pointer font-inherit flex items-center relative p-4 bg-transparent text-sm leading-normal font-semibold uppercase"
        >
          {collectionStrings.collectionShowFiltersLabel}
          {activeFilterValueCount > 0 && (
            <span className="w-6 h-6 ml-2 rounded-full bg-pageText text-pageBG text-xs leading-loose">
              {activeFilterValueCount}
            </span>
          )}
        </button>

        {activeFilterValueCount > 0 && (
          <Button
            secondary
            small
            onClick={clearFilters}
            className="hidden sm:block ml-2"
          >
            {collectionStrings.collectionClearAllLabel}
          </Button>
        )}
      </div>

      <Drawer
        direction={DrawerDirection.LEFT}
        isOpen={isDrawerOpen}
        onClose={closeFilterDrawer}
        className="max-w-md sm:max-w-sm"
      >
        <div className="flex items-center relative px-4 py-3 sm:px-8 sm:py-6 text-sm leading-normal font-semibold uppercase">
          <div className="flex items-center h-12 mr-auto">
            {collectionStrings.collectionFilters}
            {activeFilterValueCount > 0 && (
              <span className="opacity-30 ml-2">{activeFilterValueCount}</span>
            )}
          </div>

          <div className="flex items-center">
            {activeFilterValueCount > 0 && (
              <Button
                secondary
                small
                className="hidden sm:block ml-2"
                onClick={() => {
                  clearFilters()
                  closeFilterDrawer()
                }}
              >
                {collectionStrings.collectionClearAllLabel}
              </Button>
            )}

            <button
              className="appearance-none no-underline cursor-pointer font-inherit bg-transparent -mr-2 mx-2 p-2 text-sm leading-normal font-semibold uppercase"
              onClick={closeFilterDrawer}
            >
              {collectionStrings.collectionFilterModalClose}
            </button>
          </div>
          <div className="absolute inset-x-0 bottom-0 border-t" />
        </div>

        <div className="flex-1 flex flex-col overflow-y-scroll p-4 sm:px-8 sm:py-2">
          {filterGroups.map((group) => (
            <CollectionFilterGroup
              key={group._key}
              group={group}
              activeFilter={activeFilters.find(
                ({ name }) => name === group.slug
              )}
              onChange={onChange}
            />
          ))}
        </div>

        <div className="relative px-4 pb-4 sm:px-8 sm:pb-8">
          <div className="border-t pt-4 sm:pt-8">
            <Button
              primary
              large
              disabled={itemTotal === 0}
              onClick={closeFilterDrawer}
              className="w-full"
            >
              {itemTotal > 0
                ? collectionStrings.collectionFilterResultsLabel?.replace(
                    /{count}/gi,
                    `${itemTotal}`
                  )
                : collectionStrings.collectionFilterNoResultsLabel}
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  )
}

export default CollectionFilter
